// @todo: Migrate to 'Slider' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import SliderInput from '@wandb/weave/common/components/elements/SliderInput';
import {expandRangeToNElements} from '@wandb/weave/common/util/data';
import {SliderKeyboardOperation} from '@wandb/weave/common/util/media';
import {clsx} from 'clsx';
import {clamp} from 'lodash';
import React, {useCallback} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Popup} from 'semantic-ui-react';

import {SelectableAxis} from '../Axis';
import {useSectionSettingsContextMaybe} from '../WorkspaceDrawer/Settings/SectionSettingsContextProvider';
import {useMaybeWorkspaceSettingsContext} from '../WorkspaceDrawer/Settings/WorkspaceSettingsContextProvider';
import {MediaBrowserPanelConfig} from './common';
import {Layout, MediaBrowserPanelProps, RangeSettings} from './Component';

type SliderKeyboardBindings = {
  keyboardBindings?: {
    [key: string]: SliderKeyboardOperation;
  };
};

type PanelFooterProps = {
  config: Omit<MediaBrowserPanelConfig, 'chartTitle'>;
  updateConfig: MediaBrowserPanelProps['updateConfig'];
  // NOTE:  These values are derived from config. We could move these out of
  // the argument and into helper function .e.g: getActiveAxes
  sliders: {
    step: RangeSettings &
      SliderKeyboardBindings & {
        key?: string;
        steps: number[];
        strideLength: number;
      };
    index: RangeSettings;
  };
  layout: Layout;
  isMaxStepLoading: boolean;
  mediaBrowserSize: {
    width: number;
    height: number;
  };
};

const PanelFooterComp = (props: PanelFooterProps) => {
  const {
    config,
    sliders,
    updateConfig,
    layout,
    isMaxStepLoading,
    mediaBrowserSize,
  } = props;
  const {snapToExistingStep, stepStrideLength, mediaSliderKey} = config;
  const sectionSettingsContext = useSectionSettingsContextMaybe();
  const setSectionSliderSteps = sectionSettingsContext?.setSectionSliderSteps;
  const workspaceSettingsContext = useMaybeWorkspaceSettingsContext();
  const setWorkspaceSliderSteps =
    workspaceSettingsContext?.media?.setWorkspaceSliderSteps;
  const minStep = sliders.step.min;
  const maxStep = sliders.step.max;
  const stepSliderSteps = sliders.step.steps;
  const currentStepIndex = clamp(sliders.step.current, minStep, maxStep);
  const stepSliderKeyIsStep =
    !sliders.step.key || sliders.step?.key === '_step';
  const stepSliderLabel = sliders.step.label ?? 'Step';

  const maxMedia = sliders.index.max;
  const currentMediaIndex = sliders.index.current;

  // part of the fix to get slider ticks to show up with non-uniform steps
  const shouldStepSliderAllowAnyValue = stepSliderSteps.some(
    step => step % 1 !== 0
  );

  const updateStep = useCallback(
    (value: number) => {
      // When panning to the last step, set it back to auto-update
      const newValue = clamp(value, minStep, maxStep);
      const isStep = (mediaSliderKey ?? '_step') === '_step';
      updateConfig({
        stepIndex: isStep && newValue >= maxStep ? undefined : newValue,
      });
      setSectionSliderSteps?.(newValue, mediaSliderKey ?? '_step');
      setWorkspaceSliderSteps?.(
        newValue,
        mediaSliderKey ?? '_step',
        sectionSettingsContext?.sectionMediaSettings?.sliderSync ?? false
      );
    },
    [
      minStep,
      maxStep,
      updateConfig,
      setSectionSliderSteps,
      setWorkspaceSliderSteps,
      mediaSliderKey,
      sectionSettingsContext?.sectionMediaSettings?.sliderSync,
    ]
  );
  const stepSliderActive =
    sliders.step.active && maxStep > 0 && minStep < maxStep;

  const indexSliderActive = sliders.index.active && maxMedia > 0;

  const controlSlidersActive = stepSliderActive || indexSliderActive;

  const controlSliders = (stepSliderActive || indexSliderActive) && (
    <>
      <div className={'control-popup__slider-container control-popup__item'}>
        {stepSliderActive && (
          <div
            className="control-popup__control-slider"
            data-test="step-slider">
            <Popup
              trigger={
                <label data-test="step-slider-label">{stepSliderLabel}</label>
              }
              popperModifiers={{
                preventOverflow: {
                  // prevent popper from erroneously constraining the popup to the
                  // table header
                  boundariesElement: 'viewport',
                },
              }}
              disabled={!stepSliderKeyIsStep}
              content={
                'This increments every time you call wandb.log in your script'
              }
            />
            <SliderInput
              min={minStep}
              max={maxStep}
              minLabel=""
              maxLabel=""
              step={shouldStepSliderAllowAnyValue ? 'any' : 1}
              ticks={snapToExistingStep ? stepSliderSteps : undefined}
              value={currentStepIndex}
              keyboardBindings={sliders.step.keyboardBindings}
              strideLength={stepStrideLength}
              hasInput
              debounceTime={10}
              onChange={updateStep}
              showTicks={false}
            />
          </div>
        )}

        {indexSliderActive && (
          <div className="control-popup__control-slider">
            <Popup
              trigger={<label htmlFor="panelMediaBrowserIndex">Index</label>}
              popperModifiers={{
                preventOverflow: {
                  // prevent popper from erroneously constraining the popup to the
                  // table header
                  boundariesElement: 'viewport',
                },
              }}
              content={
                'This is the index in the array of examples in your wandb.log call'
              }
            />
            <SliderInput
              // @ts-expect-error - Weave component needs fixing
              id="panelMediaBrowserIndex"
              min={0}
              max={maxMedia}
              minLabel="0"
              maxLabel={maxMedia.toString()}
              step={1}
              value={currentMediaIndex}
              hasInput
              onChange={configValue => updateConfig({mediaIndex: configValue})}
              showTicks={false}
            />
          </div>
        )}
      </div>
    </>
  );
  // Sampled x-Axis data
  const xAxisData = layout.gridAxisData?.x;
  // const yAxisData =
  //   layout.axisData instanceof Array ? null : layout.axisData.y;
  // const xAxisZoom = config.zoom?.xAxis;
  const allXAxisValues = layout.allXAxisValues;

  const showXAxisSettings =
    config.gridSettings?.xAxis === 'step' &&
    xAxisData != null &&
    layout.xAxisTicks != null &&
    allXAxisValues != null &&
    (config.columnCount ?? 1) > 1;

  const showXAxis =
    showXAxisSettings &&
    xAxisData != null &&
    layout.xAxisTicks != null &&
    allXAxisValues != null &&
    !isMaxStepLoading;
  return (
    <>
      {controlSlidersActive && (
        <div className={clsx('control-popup__container')}>{controlSliders}</div>
      )}
      {showXAxis && (
        <SelectableAxis
          axisLabel="Steps" // showXAxisSettings is only true when config.gridSettings?.xAxis === 'step'
          ticks={layout.xAxisTicks!} // we know it's not null because we check for it in the showXAxis check
          direction="bottom"
          width={mediaBrowserSize.width}
          // Use axis select to indicate zoom
          selection={config.selection?.xAxis}
          onSelect={range => {
            if (config.columnCount) {
              const newRange = expandRangeToNElements(
                range,
                allXAxisValues,
                config.columnCount
              );
              props.updateConfig({
                selection: {...config.selection, xAxis: newRange},
              });

              return newRange;
            } else {
              return range;
            }
          }}
          onClear={() => {
            props.updateConfig({
              selection: {...config.selection, xAxis: undefined},
            });
          }}
        />
      )}
    </>
  );
};

export const PanelFooter = React.memo(PanelFooterComp);
